import * as React from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { SiteFooter } from '@/components/layout/site-footer';
import { SiteHeader } from '@/components/layout/site-header';

/** Scroll to `#hash` targets after client-side navigation (e.g. /#what-we-fund). */
function HashScroll() {
  const { pathname, hash } = useLocation();

  React.useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      return;
    }

    const id = decodeURIComponent(hash.replace(/^#/, ''));
    if (!id) return;

    const frame = window.requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });

    return () => window.cancelAnimationFrame(frame);
  }, [pathname, hash]);

  return null;
}

/**
 * Public microsite shell — light editorial layout inspired by ICONIQ Impact.
 * Sticky header + main content + Tour De Cure footer.
 */
export function AppShell() {
  return (
    <div className="bg-background text-foreground relative flex min-h-svh flex-col">
      <a
        href="#main-content"
        className="bg-secondary text-foreground ring-ring sr-only focus:not-sr-only focus:absolute focus:top-3 focus:left-3 focus:z-50 focus:rounded-md focus:px-3 focus:py-2 focus:text-sm focus:ring-2"
      >
        Skip to main content
      </a>

      {/* Soft paper wash — no dark particle field */}
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden" aria-hidden>
        <div className="absolute inset-0 bg-[#f7f6f3]" />
        <div className="absolute -top-32 right-0 h-[28rem] w-[28rem] rounded-full bg-primary/[0.04] blur-3xl" />
        <div className="absolute bottom-0 left-0 h-[22rem] w-[22rem] rounded-full bg-cta/[0.03] blur-3xl" />
      </div>

      <SiteHeader />

      <main id="main-content" tabIndex={-1} className="relative z-0 flex-1 outline-none">
        <HashScroll />
        <Outlet />
      </main>

      <SiteFooter />
    </div>
  );
}
